import { html, css, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import { saveYoutubeItem, updateYoutubeItem } from '../../store/youtube/youtube.js';
import {
    extractYoutubeData,
    fetchYoutubeMetadata,
} from '../../store/youtube/helpers.js'
import { navigateBack } from '../../store/route/route.js';
import { previewElearning } from '../../store/preview/preview.js';
import { translate } from '../../helpers/translate.js';
import { createSupplierKey } from '../../store/elearning/helpers.js';
import '@sdb-develop/learning-frontend';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/iron-form/iron-form.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-overlay-layout.js';

@customElement('sdb-youtube-item')
class SdbYoutubeItem extends connect(store)(LitElement) {

    @property({ type: String })
    accessor targetId;

    @state()
    accessor youtubeItem;

    @state()
    accessor lastVersion;

    @state()
    accessor isLoading = false;

    @state()
    accessor isDirty = false;

    @state()
    accessor urlInputDirty = false;

    @query('#youtube_item_form')
    accessor youtubeItemForm;

    @query('#youtube_uploader')
    accessor youtubeUploader;

    @query('#url_input')
    accessor urlInput;

    get latestVersion() {
        if (!this.youtubeItem.versions?.length)
            return null;
        return this.youtubeItem.versions[this.youtubeItem.versions.length-1];
    }

    stateChanged(state) {
        this.youtubeItem = state.youtube?.current;
        this.lastVersion = state.youtube?.current?.lastVersion;
        this.isLoading = state.youtube?.status === 'loading' || !state.youtube?.current;
        this.isDirty = state.youtube?.dirty;
        this.settings = state.env.settings;
        this.user = state.auth.user;
        this.portalId = state.env.portalIdentifier;
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                sdb-content-card {
                    max-width: 1400px;
                    margin-bottom: 16px;
                }

                sdb-content-card .headline {
                    display: inline-flex;
                    align-items: center;
                    color: var(--text-primary);
                    padding: 10px 0 20px 0;
                }

                sdb-content-card .headline span {
                    margin-left: 24px;
                    font-size: 16px;
                    font-family: var(--font-family);
                }

                paper-input {
                    margin-bottom: 5px;
                }

                paper-input[required] label {
                    color: red;
                }

                paper-toggle-button {
                    margin-top: 15px;
                    margin-bottom: 15px;
                }

                vaadin-select {
                    width: 30%;
                    min-width: 200px;
                }

                .uploader_container {
                    margin-top: 30px;
                }

                .uploader_container > span {
                    display: block;
                    font-size: 14px;
                    margin-bottom: 10px;
                    margin-left: 25px;
                }

                .medium {
                    width: 100%;
                    max-width: 700px;
                }

                .small {
                    width: 100%;
                    max-width: 300px;
                }

                .helper {
                    display: block;
                    margin-top: -10px;
                    margin-bottom: 5px;
                    font-size: 12px;
                    color: rgba(0, 0, 0, 0.6);
                }

                .clear-btn:hover {
                    transform: var(--sdb-button-scale-factor, scale(1.05));
                    cursor: pointer;
                }
            `,
        ];
    }

    render() {
        if (this.isLoading || this.targetId !== this.youtubeItem.targetId) {
            return html`<sdb-loading></sdb-loading>`;
        }

        const update = (changes) => store.dispatch(updateYoutubeItem({ changes }));
        const updateLastVersion = (changes) => store.dispatch(updateYoutubeItem({
            changes: {
                lastVersion: changes
            }
        }));

        return html`
            <sdb-overlay-layout
                headline=${this.youtubeItem.title}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('youtube')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        id="preview_button"
                        label=${translate('preview')}
                        primary
                        ?disabled=${this.isDirty}
                        @click=${()=> { store.dispatch(previewElearning({ item: this.youtubeItem })); }}>
                    </sdb-content-button>
                    <sdb-content-button
                        id="save_button"
                        label=${translate('save')}
                        primary
                        ?disabled=${!this.isDirty}
                        @click=${() => {
                            if (this.youtubeItemForm.validate())
                                store.dispatch(saveYoutubeItem({ id: this.youtubeItem.id }));
                        }}>
                    </sdb-content-button>
                </div>

                <iron-form id="youtube_item_form" slot="content">
                    <form>
                        <sdb-layout-100>

                            <sdb-content-card id="general_card">
                                <paper-input
                                    id="name_input"
                                    transparent
                                    label=${translate('name')}
                                    value=${this.youtubeItem.title}
                                    @change=${(e) => update({ title: e.target.value })}>
                                </paper-input>

                                <paper-input
                                    id="description_textarea"
                                    transparent
                                    label=${translate('description')}
                                    value=${this.youtubeItem.description}
                                    @change=${(e) => update({ description: e.target.value })}>
                                </paper-input>

                                <paper-input
                                    id="supplier_input"
                                    transparent
                                    label=${translate('supplier')}
                                    value=${this.youtubeItem.supplier.name}
                                    @change=${(e) => update({
                                        supplier: {
                                            name: e.target.value,
                                            supplierKey: createSupplierKey(e.target.value),
                                        }
                                    })}>
                                </paper-input>

                            </sdb-content-card>

                            <sdb-content-card>
                                <sdb-layout-80-20>
                                    <div slot="left">
                                        <paper-input
                                            id="url_input"
                                            label=${translate('youtube_url')}
                                            value=${this.lastVersion.startData.url}
                                            @value-changed=${(e) => this.urlInputDirty = this.lastVersion.startData.url !== this.urlInput.value}>
                                            <sdb-content-icon
                                                icon="fal fa-xmark"
                                                slot="suffix"
                                                class="clear-btn"
                                                @click=${() => {
                                                    this.urlInput.value = '';
                                                    this.urlInput.inputElement.focus();
                                                }}>
                                            </sdb-content-icon>
                                        </paper-input>
                                        <span class="helper">${translate('youtubeUrlParseHelper')}</span>
                                    </div>
                                    <div slot="right">
                                        <sdb-content-button
                                            secondary
                                            label=${translate('parseUrl')}
                                            ?disabled=${!this.urlInputDirty}
                                            @click=${() => {
                                                this._parseUrl(this.urlInput.value);
                                                this.urlInputDirty = false;
                                            }}>
                                        </sdb-content-button>
                                    </div>
                                </sdb-layout-80-20>
                            </sdb-content-card>

                            <sdb-content-card>
                                <sdb-layout-50-50>
                                    <div slot="left">
                                        <paper-input
                                            readonly
                                            id="videoid_input"
                                            class="small"
                                            label=${translate('youtube_videoId')}
                                            value=${this.lastVersion.startData.videoId}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    videoId: e.target.value,
                                                }
                                            })}>
                                        </paper-input>

                                        <paper-toggle-button
                                            id="autoplay_toggle"
                                            ?checked=${this.lastVersion.startData.autoplay}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    autoplay: e.target.checked,
                                                }
                                            })}>
                                            <span>${translate('youtube_autoplay')}</span>
                                        </paper-toggle-button>


                                        <paper-toggle-button
                                            id="mute_toggle"
                                            ?checked=${this.lastVersion.startData.mute}
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    mute: e.target.checked,
                                                }
                                            })}>
                                            <span>${translate('youtube_mute')}</span>
                                        </paper-toggle-button>

                                        <paper-input
                                            id="startAt_input"
                                            class="small"
                                            label=${translate('youtube_startAt')}
                                            placeholder="00:00:00"
                                            type="time"
                                            step="1"
                                            value="${this._formatTime(this.lastVersion.startData.startAt)}"
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    startAt: this._parseTime(e.target.value),
                                                }
                                            })}>
                                        </paper-input>

                                        <paper-input
                                            id="endAt_input"
                                            class="small"
                                            label=${translate('youtube_endAt')}
                                            placeholder="00:00:00"
                                            type="time"
                                            step="1"
                                            value="${this._formatTime(this.lastVersion.startData.endAt)}"
                                            @change=${(e) => updateLastVersion({
                                                startData: {
                                                    endAt: this._parseTime(e.target.value),
                                                }
                                            })}>
                                        </paper-input>
                                    </div>
                                    <div slot="right">
                                        <img src=${this.lastVersion.startData.thumbnailUrl}>
                                    </div>
                                </sdb-layout-50-50>

                            </sdb-content-card>

                        </sdb-layout-100>
                    </form>
                </iron-form>
            </sdb-overlay-layout>
        `;
    }

    _reverse(items) {
        return [...items].reverse();
    }

    _parseTime(value) {
        if (!value) return 0;

        const parts = value.split(':').map(Number);

        return parts.length === 3
            ? parts[0] * 3600 + parts[1] * 60 + parts[2]
            : parts.length === 2
                ? parts[0] * 60 + parts[1]
                : Number(value) || 0;
    }

    _formatTime(seconds) {
        if (!seconds) return '00:00:00';

        const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
        const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');

        return `${h}:${m}:${s}`;
    }

    async _parseUrl(url) {
        const {
            videoId,
            startAt,
            endAt,
        } = extractYoutubeData(url);
        if (!videoId)
            return;

        const {
            title,
            author_name,
            thumbnail_url,
        } = await fetchYoutubeMetadata(videoId);

        store.dispatch(updateYoutubeItem({
            changes: {
                title: title,
                supplier: {
                    name: author_name,
                    supplierKey: createSupplierKey(author_name),
                },
                image: thumbnail_url,
                thumbnail: thumbnail_url,
                lastVersion: {
                    title: title,
                    startData: {
                        url: url,
                        videoId: videoId,
                        startAt: startAt,
                        endAt: endAt,
                        thumbnailUrl: thumbnail_url,
                    },
                }
            }
        }));
    }
}
